import {
  AlertTriangle,
  Check,
  Copy,
  CreditCard,
  Lock,
  type LucideIcon,
  ShieldCheck,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";

import { KycStatusPill } from "@/modules/verification/components/KycStatusPill";
import { resolveRejectionDescription } from "@/modules/verification/constants/verification.constants";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaLineSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { useCopyToClipboard } from "@/shared/hooks/useCopyToClipboard";
import { useCountryLabel } from "@/shared/hooks/useCountryLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime, formatTimeAgo } from "@/shared/utils/dateTimeHelpers";

import {
  ORGANIZATION_STATUS_LABEL_KEYS,
  ORGANIZATION_STATUS_PILL,
} from "../constants/organizationsAdmin.constants";
import type { OrganizationOverviewState } from "../hooks/useOrganizationOverview";
import type { AdminOrganization, OrganizationOverview } from "../types/organizationsAdmin.types";
import {
  CARD_STAGE_LABEL_KEYS,
  CARD_STAGE_PILL,
  CARDHOLDER_STAGE_LABEL_KEYS,
  cardholderStages,
  cardProviderLabelKey,
  lastSignedIn,
  PAUSE_REASON_LABEL_KEYS,
} from "../utils/organizationOverview";

const MICRO_LABEL = "font-mono text-[10px] uppercase tracking-wider text-muted-foreground";

const WHOLE_UNIT_CURRENCIES = new Set(["COP", "CLP"]);

export interface OrganizationOverviewPanelProps {
  /** The listing row: its fields render at once, before the overview answers. */
  organization: AdminOrganization;
  state: OrganizationOverviewState;
  onRetry: () => void;
}

export function OrganizationOverviewPanel({ organization, state, onRetry }: OrganizationOverviewPanelProps) {
  const { t } = useTranslations("organizations");
  const overview = state.status === "ready" ? state.overview : null;

  const blocked = state.status === "error" || state.status === "forbidden";

  return (
    <div className="space-y-6">
      {state.status === "forbidden" ? (
        <ColocaAlert variant="warning" icon={Lock} text={t("overview_forbidden")} />
      ) : state.status === "error" ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertTriangle}
          text={t("overview_load_error")}
          inline
          actions={
            <ColocaButton variant="secondary" size="sm" onClick={onRetry}>
              {t("action_retry")}
            </ColocaButton>
          }
        />
      ) : (
        <section aria-labelledby="organization-standing-title">
          <h3 id="organization-standing-title" className="text-sm font-semibold text-foreground">
            {t("overview_status_title")}
          </h3>
          <ul className="mt-3 divide-y divide-border rounded-lg border border-border">
            {overview ? (
              <>
                <VerificationRow overview={overview} />
                <CardsRow overview={overview} />
                <AccessRow overview={overview} />
              </>
            ) : (
              [0, 1, 2].map((row) => (
                <li key={row} className="p-4">
                  <ColocaLineSkeleton lines={2} />
                </li>
              ))
            )}
          </ul>
        </section>
      )}

      <DetailsSection
        organization={organization}
        overview={overview}
        loading={state.status === "loading" || state.status === "idle"}
      />

      {!blocked && <ActivitySection overview={overview} />}
    </div>
  );
}

function StandingRow({
  icon: Icon,
  label,
  status,
  children,
}: {
  icon: LucideIcon;
  label: ReactNode;
  status: ReactNode;
  children?: ReactNode;
}) {
  return (
    <li className="flex items-start gap-3 p-4">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Icon className="size-4" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium text-foreground">{label}</p>
          {status}
        </div>
        {children && <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">{children}</div>}
      </div>
    </li>
  );
}

function VerificationRow({ overview }: { overview: OrganizationOverview }) {
  const { t, activeLanguage } = useTranslations("organizations");
  const { t: tVerification } = useTranslations("verification");
  const { verification, identity } = overview;

  const purpose = verification.verification.purpose;
  const document = [identity?.identification_type, identity?.identification_number].filter(Boolean).join(" ");
  const subject = [
    purpose ? t(purpose === "KYB" ? "overview_purpose_kyb" : "overview_purpose_kyc") : null,
    identity ? t(identity.type === "business" ? "identity_type_business" : "identity_type_individual") : null,
    document || null,
    identity?.name ?? null,
  ]
    .filter(Boolean)
    .join(" · ");

  const statusLine = (() => {
    if (!verification.has_corporate_identity) return t("overview_verification_none");
    switch (verification.kyc_status) {
      case "VERIFIED":
        return verification.verified_at
          ? t("overview_verification_verified_at", {
              date: formatDateTime(verification.verified_at, { locale: activeLanguage, includeTime: false }),
            })
          : null;
      case "IN_REVIEW":
        return t("overview_verification_in_review");
      case "REJECTED":
        return resolveRejectionDescription(verification.rejection_reason ?? undefined, tVerification);
      case "EXPIRED":
        return t("overview_verification_expired");
      default:
        return t("overview_verification_pending");
    }
  })();

  return (
    <StandingRow
      icon={ShieldCheck}
      label={t("overview_verification_label")}
      status={<KycStatusPill status={verification.kyc_status} />}
    >
      {subject && <p className="text-foreground">{subject}</p>}
      {statusLine && <p>{statusLine}</p>}
    </StandingRow>
  );
}

function CardsRow({ overview }: { overview: OrganizationOverview }) {
  const { t } = useTranslations("organizations");
  const { provider, organization_identity: own, cardholders } = overview.card_onboarding;
  const providerKey = cardProviderLabelKey(provider);
  const stages = cardholderStages(cardholders);

  if (!providerKey) {
    return (
      <StandingRow icon={CreditCard} label={t("overview_cards_label")} status={null}>
        <p>{t("overview_cards_no_provider")}</p>
      </StandingRow>
    );
  }

  return (
    <StandingRow
      icon={CreditCard}
      label={t("overview_cards_provider_label", { provider: t(providerKey) })}
      status={
        <ColocaStatusPill variant={own ? CARD_STAGE_PILL[own.stage] : "neutral"} size="sm" withDot>
          {own ? t(CARD_STAGE_LABEL_KEYS[own.stage]) : t("overview_cards_own_none")}
        </ColocaStatusPill>
      }
    >
      {own?.pause_reason && (
        <p className="font-medium text-warning">
          {t("overview_cards_paused", { reason: t(PAUSE_REASON_LABEL_KEYS[own.pause_reason]) })}
        </p>
      )}
      <p>
        {stages.length === 0
          ? t("overview_cards_holders_none")
          : `${t("overview_cards_holders_label")}: ${stages
              .map(({ stage, count }) => `${t(CARDHOLDER_STAGE_LABEL_KEYS[stage])} ${count}`)
              .join(" · ")}`}
      </p>
    </StandingRow>
  );
}

function AccessRow({ overview }: { overview: OrganizationOverview }) {
  const { t, activeLanguage } = useTranslations("organizations");
  const { counts, items } = overview.users;
  const latest = lastSignedIn(items);

  return (
    <StandingRow
      icon={Users}
      label={t("overview_access_label")}
      status={
        <ColocaStatusPill variant={counts.active > 0 ? "success" : "neutral"} size="sm" withDot>
          {t("overview_access_active", { count: counts.active })}
        </ColocaStatusPill>
      }
    >
      <p>
        {counts.total === 0
          ? t("overview_access_none")
          : latest?.last_login_at
            ? t("overview_access_last_login", {
                when: formatTimeAgo(latest.last_login_at, { locale: activeLanguage }),
                name: latest.full_name,
              })
            : t("overview_access_never")}
      </p>
    </StandingRow>
  );
}

function DetailsSection({
  organization,
  overview,
  loading,
}: {
  organization: AdminOrganization;
  overview: OrganizationOverview | null;
  loading: boolean;
}) {
  const { t, activeLanguage } = useTranslations("organizations");
  const countryLabel = useCountryLabel();
  const { copiedValue, copy } = useCopyToClipboard();
  const createdBy = overview?.organization.created_by;

  return (
    <section aria-labelledby="organization-details-title">
      <h3 id="organization-details-title" className="text-sm font-semibold text-foreground">
        {t("overview_details_title")}
      </h3>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-4 rounded-lg border border-border p-4">
        <Fact label={t("col_status")}>
          <ColocaStatusPill variant={ORGANIZATION_STATUS_PILL[organization.status]} size="sm" withDot>
            {t(ORGANIZATION_STATUS_LABEL_KEYS[organization.status])}
          </ColocaStatusPill>
        </Fact>
        <Fact label={t("col_country")}>
          {organization.country ? countryLabel(organization.country, organization.country) : t("value_none")}
        </Fact>
        <Fact label={t("detail_phone")}>
          {loading ? (
            <ColocaLineSkeleton />
          ) : (
            <span className="font-mono text-xs tabular-nums">
              {overview?.organization.phone ?? t("value_none")}
            </span>
          )}
        </Fact>
        <Fact label={t("col_created")}>
          <span className="font-mono text-xs tabular-nums">
            {formatDateTime(organization.created_at, { locale: activeLanguage, includeTime: false })}
          </span>
          {createdBy && (
            <span className="mt-0.5 block text-xs text-muted-foreground">
              {t("detail_created_by", { name: createdBy.full_name || createdBy.email })}
            </span>
          )}
        </Fact>
        <div className="col-span-2">
          <dt className={MICRO_LABEL}>{t("detail_id")}</dt>
          <dd className="mt-1 flex items-center gap-2">
            <span className="min-w-0 break-all font-mono text-xs text-foreground">{organization.id}</span>
            <ColocaIconButton
              icon={copiedValue === organization.id ? Check : Copy}
              variant="ghost"
              size="xs"
              aria-label={t("copy_id_aria")}
              onClick={() => void copy(organization.id, t("id_copied"))}
            />
          </dd>
        </div>
      </dl>
    </section>
  );
}

function ActivitySection({ overview }: { overview: OrganizationOverview | null }) {
  const { t, activeLanguage } = useTranslations("organizations");
  const ago = (value: string) => formatTimeAgo(value, { locale: activeLanguage });

  const lastOperation = (at: string | null, count: number) =>
    at ? t("activity_last_value", { when: ago(at), count }) : t("activity_never");

  return (
    <section aria-labelledby="organization-activity-title">
      <h3 id="organization-activity-title" className="text-sm font-semibold text-foreground">
        {t("overview_activity_title")}
      </h3>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-4 rounded-lg border border-border p-4">
        {overview ? (
          <>
            <Fact label={t("activity_wallets")} wide>
              {overview.wallets.length === 0 ? (
                t("activity_wallets_none")
              ) : (
                <ul className="flex flex-wrap gap-x-5 gap-y-1">
                  {overview.wallets.map((wallet) => (
                    <li key={wallet.currency}>
                      <ColocaAmountDisplay
                        value={wallet.balance_available}
                        currency={wallet.currency}
                        fractionDigits={WHOLE_UNIT_CURRENCIES.has(wallet.currency) ? 0 : 2}
                      />
                      {wallet.status !== "active" && (
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          {t(wallet.status === "frozen" ? "wallet_frozen" : "wallet_closed")}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Fact>
            <Fact label={t("activity_last_payin")}>
              {lastOperation(overview.activity.last_payin_at, overview.activity.payins_last_30_days)}
            </Fact>
            <Fact label={t("activity_last_payout")}>
              {lastOperation(overview.activity.last_payout_at, overview.activity.payouts_last_30_days)}
            </Fact>
            <Fact label={t("activity_cards")}>
              {overview.cards.total === 0
                ? t("activity_none")
                : t("activity_cards_value", {
                    total: overview.cards.total,
                    active: overview.cards.active,
                    frozen: overview.cards.frozen,
                  })}
            </Fact>
            <Fact label={t("activity_holders")}>{overview.clients.total}</Fact>
            <Fact label={t("activity_breb_keys")}>
              {overview.breb_keys.active === 0 ? (
                t("activity_none")
              ) : (
                <>
                  {overview.breb_keys.active}
                  {overview.breb_keys.primary_key && (
                    <span className="mt-0.5 block break-all font-mono text-xs text-muted-foreground">
                      {overview.breb_keys.primary_key}
                    </span>
                  )}
                </>
              )}
            </Fact>
            <Fact label={t("activity_api_keys")}>
              {overview.api_keys.active === 0
                ? t("activity_none")
                : overview.api_keys.last_used_at
                  ? t("activity_api_keys_used", {
                      count: overview.api_keys.active,
                      when: ago(overview.api_keys.last_used_at),
                    })
                  : t("activity_api_keys_value", { count: overview.api_keys.active })}
            </Fact>
          </>
        ) : (
          [0, 1, 2, 3, 4, 5].map((cell) => (
            <div key={cell}>
              <ColocaLineSkeleton lines={2} />
            </div>
          ))
        )}
      </dl>
    </section>
  );
}

function Fact({ label, wide, children }: { label: string; wide?: boolean; children: ReactNode }) {
  return (
    <div className={wide ? "col-span-2" : undefined}>
      <dt className={MICRO_LABEL}>{label}</dt>
      <dd className="mt-1.5 text-sm text-foreground">{children}</dd>
    </div>
  );
}
